import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { PermissionNames } from '../../modules/admin/models/permission-names';
import { UserService } from '../../modules/admin/services/user.service';
import { Role } from '../../modules/admin/constants/role';
import { environment } from '../../../environments/environment';
import { Align, PopupComponent } from '@progress/kendo-angular-popup';
import { Router, NavigationEnd, RouterLink } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import { MixpanelService } from '../../services/mixpanel.service';
import { DashboardReportFormService } from '../../modules/rail-performance/submodules/layout/services/dashboard-report-form.service';
import { NgClass, AsyncPipe } from '@angular/common';
import { SvgIconComponent } from 'angular-svg-icon';
import { LoginButtonComponent } from '../../modules/authentication/components/login-button/login-button.component';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        RouterLink,
        NgClass,
        SvgIconComponent,
        LoginButtonComponent,
        PopupComponent,
    ],
})
export class HeaderComponent {
    private router = inject(Router);
    private dashboardReportFormService = inject(DashboardReportFormService);
    private mixpanelService = inject(MixpanelService);
    userService = inject(UserService);

    readonly user = toSignal(this.userService.getCurrentUser());

    readonly currentRoute = toSignal(
        this.router.events.pipe(
            filter(
                (event): event is NavigationEnd =>
                    event instanceof NavigationEnd,
            ),
            map(() => this.router.url),
            startWith(this.router.url),
        ),
        { initialValue: this.router.url },
    );

    get canAccessPlatformManagement(): boolean {
        return (
            (this.user()?.hasPermission(
                PermissionNames.PlatformManagement.Tenants.Read,
            ) ||
                this.user()?.hasPermission(
                    PermissionNames.PlatformManagement.Users.Read,
                ) ||
                this.user()?.hasPermission(
                    PermissionNames.PlatformManagement.Roles.Read,
                ) ||
                this.user()?.hasPermission(
                    PermissionNames.PlatformManagement.Permissions.Read,
                )) ??
            false
        );
    }

    get canAccessSupportCenter(): boolean {
        return (
            (this.user()?.hasPermission(
                PermissionNames.PlatformManagement.KnowledgeBase.Read,
            ) ||
                this.user()?.hasPermission(
                    PermissionNames.RailPerformance.KnowledgeBase.Read,
                ) ||
                this.user()?.hasPermission(
                    PermissionNames.ForecastDemand.KnowledgeBase.Read ||
                        this.user()?.hasPermission(
                            PermissionNames.RailImpact.KnowledgeBase.Read,
                        ),
                )) ??
            false
        );
    }

    get hideGearMenu(): boolean {
        const u = this.user();
        if (u && u.roles.length > 0) {
            return !u.roles.some(
                (role) =>
                    role.name.toLowerCase() ===
                    Role.SuperAdministrator.toLowerCase(),
            );
        }
        return true;
    }

    showLogin: boolean = false;
    showGearMenu: boolean = false;
    animatePopup = false;
    public popupAlign: Align = { horizontal: 'right', vertical: 'top' };
    public areaData = [
        {
            categoryName: 'Dashboard',
        },
        {
            categoryName: 'Storage',
            subCategories: [
                { subCategoryName: 'Wall Shelving' },
                { subCategoryName: 'Floor Shelving' },
                { subCategoryName: 'Kids Storage' },
            ],
        },
    ];
    loggedIn = false;

    public idleState = 'NOT_STARTED';
    public lastPing?: Date = new Date();
    public openMenuModal = false;
    public showActive = false;
    public isRNPMenu = false;
    protected enableRNPMenu: boolean = false;
    protected showSupportCenterButton: boolean = false;

    Role = Role;
    PermissionNames = PermissionNames;

    onAnchorViewportLeave(): void {
        this.showGearMenu = false;
    }

    isRouteActive(route: string): boolean {
        return this.currentRoute() === route;
    }

    get trinityRailMarketUrl(): string {
        return environment.trinityRailMarketUrl;
    }

    openLogin() {
        if (this.showLogin) {
            this.showLogin = false;
        } else {
            this.showLogin = true;
        }
    }

    closeLogin(e: Event) {
        if (e.target != e.currentTarget) {
            return;
        }

        if (this.showLogin) {
            this.showLogin = false;
        } else {
            this.showLogin = true;
        }
    }

    onClick(value: boolean, menuName?: string) {
        this.showActive = value;
        this.isRNPMenuClicked(menuName);

        if (menuName) {
            this.trackMenuClick(menuName);
        }
    }

    isRNPMenuClicked(menuName?: string) {
        switch (menuName) {
            case 'Rail Network Performance':
            case 'Railcar Volume':
            case 'Network Dwell':
            case 'Cycle Time':
            case 'Shipments':
            case 'Railstate Comparison':
                this.isRNPMenu = true;
                break;
            default:
                this.isRNPMenu = false;
                break;
        }
    }

    hideRailstateComparisonMenuFilter(menuName: string | undefined) {
        this.isRNPMenuClicked(menuName);
        if (
            menuName == 'Railstate Comparison' &&
            this.dashboardReportFormService.showFilters$.value
        ) {
            this.dashboardReportFormService.toggleFiltersVisible();
        }
    }

    trackMenuClick(menuName: string) {
        const userEmail = localStorage.getItem('userEmail') || '';
        const eventName = `${menuName.toLowerCase().replace(/ /g, '_')}_menu_click`;
        const eventDefinition = `User clicks on ${menuName} menu item`;

        this.mixpanelService.track({
            id: userEmail,
            eventName: eventName,
            eventDisplayName: menuName,
            kpi: 'User Engagement',
            trigger: 'On Menu Item Click',
            eventDefinition: eventDefinition,
            propertyType: 'Navigation',
            propertyDefinition: 'Menu Click',
        });
    }
}
